import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

export interface UseHeroHeightLockOptions {
  enabled?: boolean;
}

/**
 * Locks the hero element height to a fixed px value to prevent iOS browser-chrome
 * viewport changes from resizing the hero during scroll (flickering).
 * Only re-locks when the viewport width actually changes.
 */
export const useHeroHeightLock = ({ enabled = true }: UseHeroHeightLockOptions = {}) => {
  const heroRef = useRef<HTMLDivElement>(null);
  const lockedWidthRef = useRef<number>(0);

  const lockHeight = useCallback(() => {
    const el = heroRef.current;
    if (!el) return;

    const swiperEl = el.querySelector(".swiper") as HTMLElement | null;
    const height = el.offsetHeight;

    el.style.height = `${height}px`;
    el.style.overflow = "hidden";
    if (swiperEl) swiperEl.style.height = `${height}px`;
  }, []);

  const unlockHeight = useCallback(() => {
    const el = heroRef.current;
    if (!el) return;

    const swiperEl = el.querySelector(".swiper") as HTMLElement | null;

    el.style.height = "";
    el.style.overflow = "";
    if (swiperEl) swiperEl.style.height = "";
  }, []);

  useLayoutEffect(() => {
    if (!heroRef.current || !enabled) return;

    lockedWidthRef.current = window.innerWidth;
    lockHeight();

    return unlockHeight;
  }, [enabled, lockHeight, unlockHeight]);

  useEffect(() => {
    if (!enabled) return;

    const handleResize = () => {
      if (window.innerWidth === lockedWidthRef.current) return;
      lockedWidthRef.current = window.innerWidth;

      unlockHeight();
      requestAnimationFrame(lockHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [enabled, lockHeight, unlockHeight]);

  return { heroRef, lockHeight, unlockHeight, lockedWidthRef };
};
